// src/components/home/SponsoredShopsSection.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import {
  trackSponsoredClick,
  trackSponsoredImpression,
} from "../../lib/sponsoredTracking";

function normalizeTimestamp(ts) {
  if (!ts) return null;
  try {
    if (typeof ts?.toMillis === "function") return ts.toMillis();
    const d = new Date(ts);
    const t = d.getTime();
    return Number.isNaN(t) ? null : t;
  } catch {
    return null;
  }
}

function isPlacementActive(placement) {
  if (!placement || placement.isActive !== true) return false;

  const now = Date.now();
  const startAt = normalizeTimestamp(placement.startAt);
  const endAt = normalizeTimestamp(placement.endAt);

  if (startAt && now < startAt) return false;
  if (endAt && now > endAt) return false;

  return true;
}

function getShopName(item) {
  return (
    item?.shopName ||
    item?.vendorName ||
    item?.displayName ||
    item?.title ||
    "Boutique sponsorisée"
  );
}

function getShopSubtitle(item) {
  return (
    item?.subtitle ||
    item?.description ||
    item?.categories ||
    "Découvrez cette boutique partenaire"
  );
}

function getShopLogo(item) {
  return (
    item?.logoUrl ||
    item?.shopLogo ||
    item?.imageUrl ||
    ""
  );
}

function getShopBanner(item) {
  return (
    item?.bannerUrl ||
    item?.shopBanner ||
    item?.bannerDesktopUrl ||
    item?.bannerMobileUrl ||
    item?.imageUrl ||
    ""
  );
}

function getShopLink(item) {
  if (item?.redirectUrl) return item.redirectUrl;
  if (item?.vendorId) return `/shop/${item.vendorId}`;
  return "#";
}

export default function SponsoredShopsSection({
  title = "Boutiques sponsorisées",
  subtitle = "Découvrez les boutiques mises en avant par GoShop",
  placementKey = "homepage_featured_shops",
  maxPlacements = 6,
}) {
  const [loading, setLoading] = useState(true);
  const [placements, setPlacements] = useState([]);
  const [error, setError] = useState("");
  const trackedImpressionsRef = useRef(new Set());

  useEffect(() => {
    let isMounted = true;

    const loadSponsoredShops = async () => {
      setLoading(true);
      setError("");

      try {
        // 1) Charger les placements sponsorisés
        const snap = await getDocs(
          query(
            collection(db, "sponsoredPlacements"),
            where("type", "==", "shop"),
            where("placement", "==", placementKey),
            where("isActive", "==", true),
            orderBy("priority", "asc"),
            limit(maxPlacements)
          )
        );

        const rawPlacements = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        const activePlacements = rawPlacements.filter(isPlacementActive);

        // 2) Charger les infos boutiques depuis users
        const mergedPlacements = await Promise.all(
          activePlacements.map(async (placement) => {
            if (!placement?.vendorId) return placement;

            try {
              const userRef = doc(db, "users", placement.vendorId);
              const userSnap = await getDoc(userRef);

              if (!userSnap.exists()) {
                return placement;
              }

              const userData = userSnap.data() || {};

              return {
                ...placement,

                // Champs boutique venant de users si absents dans placement
                shopName:
                  placement.shopName ||
                  userData.shopName ||
                  userData.displayName ||
                  placement.vendorName ||
                  "",

                vendorName:
                  placement.vendorName ||
                  userData.shopName ||
                  userData.displayName ||
                  "",

                displayName: userData.displayName || "",

                categories:
                  placement.categories ||
                  userData.categories ||
                  "",

                city:
                  placement.city ||
                  userData.city ||
                  "",

                phone:
                  placement.phone ||
                  userData.phone ||
                  "",

                email:
                  placement.email ||
                  userData.email ||
                  "",

                logoUrl:
                  placement.logoUrl ||
                  placement.shopLogo ||
                  userData.logoUrl ||
                  "",

                bannerUrl:
                  placement.bannerUrl ||
                  placement.shopBanner ||
                  placement.bannerDesktopUrl ||
                  placement.bannerMobileUrl ||
                  userData.bannerUrl ||
                  "",
              };
            } catch (err) {
              console.error(
                "Erreur chargement user pour boutique sponsorisée:",
                placement?.vendorId,
                err
              );
              return placement;
            }
          })
        );

        if (!isMounted) return;
        setPlacements(mergedPlacements);
      } catch (err) {
        console.error("Erreur chargement boutiques sponsorisées :", err);
        if (isMounted) {
          setError("Impossible de charger les boutiques sponsorisées.");
          setPlacements([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadSponsoredShops();

    return () => {
      isMounted = false;
    };
  }, [placementKey, maxPlacements]);

  useEffect(() => {
    const run = async () => {
      for (const placement of placements) {
        if (!placement?.id) continue;
        if (trackedImpressionsRef.current.has(placement.id)) continue;

        trackedImpressionsRef.current.add(placement.id);

        await trackSponsoredImpression({
          placementId: placement.id,
          sourceDealId: placement.sourceDealId || null,
        });
      }
    };

    if (placements.length) {
      run();
    }
  }, [placements]);

  const visibleShops = useMemo(() => placements.filter(Boolean), [placements]);

  const handleSponsoredClick = async (shop) => {
    await trackSponsoredClick({
      placementId: shop.id,
      sourceDealId: shop.sourceDealId || null,
    });
  };

  if (loading) {
    return (
      <section className="ssh-section">
        <div className="ssh-header">
          <div>
            <h2>{title}</h2>
            <p>{subtitle}</p>
          </div>
        </div>

        <div className="ssh-loading">Chargement des boutiques sponsorisées...</div>
        <style>{styles}</style>
      </section>
    );
  }

  if (error) {
    return (
      <section className="ssh-section">
        <div className="ssh-header">
          <div>
            <h2>{title}</h2>
            <p>{subtitle}</p>
          </div>
        </div>

        <div className="ssh-error">{error}</div>
        <style>{styles}</style>
      </section>
    );
  }

  if (!visibleShops.length) return null;

  return (
    <section className="ssh-section">
      <div className="ssh-header">
        <div>
          <div className="ssh-kicker">Mise en avant</div>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>

        <div className="ssh-pill">
          {visibleShops.length} boutique{visibleShops.length > 1 ? "s" : ""} sponsorisée
          {visibleShops.length > 1 ? "s" : ""}
        </div>
      </div>

      <div className="ssh-grid">
        {visibleShops.map((shop) => {
          const name = getShopName(shop);
          const sub = getShopSubtitle(shop);
          const logo = getShopLogo(shop);
          const banner = getShopBanner(shop);
          const link = getShopLink(shop);

          return (
            <article key={shop.id} className="ssh-card">
              <div className="ssh-badge">Sponsorisé</div>

              <Link
                to={link}
                className="ssh-bannerWrap"
                onClick={() => handleSponsoredClick(shop)}
              >
                {banner ? (
                  <img
                    src={banner}
                    alt={name}
                    className="ssh-banner"
                    loading="lazy"
                  />
                ) : (
                  <div className="ssh-bannerFallback" />
                )}

                <div className="ssh-logoWrap">
                  {logo ? (
                    <img
                      src={logo}
                      alt={`Logo ${name}`}
                      className="ssh-logo"
                      loading="lazy"
                    />
                  ) : (
                    <div className="ssh-logoFallback">
                      {String(name || "B").charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
              </Link>

              <div className="ssh-body">
                <Link
                  to={link}
                  className="ssh-name"
                  onClick={() => handleSponsoredClick(shop)}
                >
                  {name}
                </Link>

                <p className="ssh-subtitle">{sub}</p>

                <div className="ssh-metaRow">
                  {shop?.categoryId ? (
                    <span className="ssh-chip">{shop.categoryId}</span>
                  ) : null}

                  {shop?.city ? (
                    <span className="ssh-chip">{shop.city}</span>
                  ) : null}
                </div>

                <div className="ssh-actions">
                  <Link
                    to={link}
                    className="ssh-btn ssh-btn-primary"
                    onClick={() => handleSponsoredClick(shop)}
                  >
                    Voir la boutique
                  </Link>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <style>{styles}</style>
    </section>
  );
}

const styles = `
  .ssh-section{
    margin:24px 0;
    padding:18px;
    border-radius:24px;
    background:linear-gradient(180deg,#ffffff 0%, #f8fafc 100%);
    border:1px solid #e5e7eb;
    box-shadow:0 16px 36px rgba(15,23,42,.06);
  }

  .ssh-header{
    display:flex;
    justify-content:space-between;
    align-items:flex-start;
    gap:16px;
    flex-wrap:wrap;
    margin-bottom:16px;
  }

  .ssh-kicker{
    display:inline-flex;
    align-items:center;
    padding:5px 10px;
    border-radius:999px;
    background:#ecfeff;
    color:#0f766e;
    font-size:11px;
    font-weight:900;
    letter-spacing:.08em;
    text-transform:uppercase;
    margin-bottom:8px;
  }

  .ssh-header h2{
    margin:0 0 4px;
    font-size:24px;
    font-weight:900;
    color:#0f172a;
  }

  .ssh-header p{
    margin:0;
    font-size:13px;
    color:#64748b;
    max-width:720px;
  }

  .ssh-pill{
    display:inline-flex;
    align-items:center;
    padding:8px 12px;
    border-radius:999px;
    background:#eff6ff;
    color:#1d4ed8;
    border:1px solid #dbeafe;
    font-size:12px;
    font-weight:800;
    white-space:nowrap;
  }

  .ssh-grid{
    display:grid;
    grid-template-columns:repeat(3, minmax(0,1fr));
    gap:16px;
  }

  .ssh-card{
    position:relative;
    background:#fff;
    border:1px solid #e5e7eb;
    border-radius:22px;
    overflow:hidden;
    box-shadow:0 10px 24px rgba(15,23,42,.05);
    transition:transform .18s ease, box-shadow .18s ease;
  }

  .ssh-card:hover{
    transform:translateY(-2px);
    box-shadow:0 16px 32px rgba(15,23,42,.08);
  }

  .ssh-badge{
    position:absolute;
    top:12px;
    left:12px;
    z-index:3;
    display:inline-flex;
    align-items:center;
    padding:5px 10px;
    border-radius:999px;
    background:#fff7ed;
    color:#c2410c;
    border:1px solid #fed7aa;
    font-size:11px;
    font-weight:900;
    box-shadow:0 8px 18px rgba(15,23,42,.08);
  }

  .ssh-bannerWrap{
    position:relative;
    display:block;
    width:100%;
    aspect-ratio: 16 / 9;
    background:#f8fafc;
    text-decoration:none;
  }

  .ssh-banner{
    width:100%;
    height:100%;
    object-fit:cover;
    display:block;
  }

  .ssh-bannerFallback{
    width:100%;
    height:100%;
    background:linear-gradient(135deg,#dbeafe 0%, #eff6ff 40%, #f8fafc 100%);
  }

  .ssh-logoWrap{
    position:absolute;
    left:16px;
    bottom:-28px;
    width:76px;
    height:76px;
    border-radius:20px;
    overflow:hidden;
    background:#fff;
    border:4px solid #fff;
    box-shadow:0 14px 26px rgba(15,23,42,.15);
    display:flex;
    align-items:center;
    justify-content:center;
  }

  .ssh-logo{
    width:100%;
    height:100%;
    object-fit:cover;
    display:block;
  }

  .ssh-logoFallback{
    width:100%;
    height:100%;
    display:flex;
    align-items:center;
    justify-content:center;
    font-size:24px;
    font-weight:900;
    color:#64748b;
    background:#f1f5f9;
  }

  .ssh-body{
    padding:40px 14px 14px;
    display:flex;
    flex-direction:column;
    gap:8px;
  }

  .ssh-name{
    text-decoration:none;
    color:#0f172a;
    font-size:18px;
    font-weight:900;
    line-height:1.25;
  }

  .ssh-name:hover{
    color:#2563eb;
  }

  .ssh-subtitle{
    margin:0;
    color:#64748b;
    font-size:13px;
    line-height:1.45;
    min-height:38px;
  }

  .ssh-metaRow{
    display:flex;
    gap:8px;
    flex-wrap:wrap;
  }

  .ssh-chip{
    display:inline-flex;
    align-items:center;
    padding:5px 9px;
    border-radius:999px;
    background:#f8fafc;
    border:1px solid #e5e7eb;
    color:#334155;
    font-size:11px;
    font-weight:800;
  }

  .ssh-actions{
    margin-top:4px;
    display:flex;
    gap:8px;
    flex-wrap:wrap;
  }

  .ssh-btn{
    display:inline-flex;
    align-items:center;
    justify-content:center;
    padding:9px 12px;
    border-radius:999px;
    text-decoration:none;
    font-size:12px;
    font-weight:800;
    transition:.18s ease;
  }

  .ssh-btn-primary{
    background:#1d4ed8;
    color:#fff;
    border:1px solid #1d4ed8;
  }

  .ssh-btn-primary:hover{
    background:#0f172a;
    border-color:#0f172a;
    color:#fff;
  }

  .ssh-loading,
  .ssh-error{
    font-size:13px;
    color:#64748b;
    padding:12px 0;
  }

  .ssh-error{
    color:#b91c1c;
  }

  @media (max-width: 1080px){
    .ssh-grid{
      grid-template-columns:repeat(2, minmax(0,1fr));
    }
  }

  @media (max-width: 640px){
    .ssh-section{
      padding:14px;
      border-radius:20px;
    }

    .ssh-header h2{
      font-size:20px;
    }

    .ssh-grid{
      grid-template-columns:minmax(0,1fr);
    }

    .ssh-logoWrap{
      width:68px;
      height:68px;
      bottom:-24px;
      border-radius:18px;
    }

    .ssh-body{
      padding:36px 12px 12px;
    }

    .ssh-name{
      font-size:16px;
    }
  }
`;